/**
 * @architecture-header 1
 * @layer shared
 * @domain visual-language
 * @responsibility Render the deterministic QR placeholder matrix (`qrMatrix` / `qrSVG`).
 * @state-owner none
 * @allowed-imports React
 * @forbidden real QR encoding; network; business rules
 * @public-api workspace modules
 * @side-effects none
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

/** An FNV-1a seeded xorshift fill with three finder patterns: stable per seed, not scannable. */
export function qrMatrix(seed: string, size: number): readonly (readonly number[])[] {
  let hash = 2166136261;
  for (const character of String(seed)) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  const random = () => {
    hash ^= hash << 13;
    hash >>>= 0;
    hash ^= hash >>> 17;
    hash ^= hash << 5;
    hash >>>= 0;
    return hash / 4294967296;
  };
  const matrix = Array.from({ length: size }, () => new Array<number>(size).fill(0));
  const inFinder = (x: number, y: number) =>
    (x < 8 && y < 8) || (x >= size - 8 && y < 8) || (x < 8 && y >= size - 8);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      if (!inFinder(x, y)) matrix[y][x] = random() > 0.52 ? 1 : 0;
    }
  }
  const finder = (ox: number, oy: number) => {
    for (let y = 0; y < 7; y += 1) {
      for (let x = 0; x < 7; x += 1) {
        const edge = x === 0 || x === 6 || y === 0 || y === 6;
        const core = x >= 2 && x <= 4 && y >= 2 && y <= 4;
        matrix[oy + y][ox + x] = edge || core ? 1 : 0;
      }
    }
  };
  finder(0, 0);
  finder(size - 7, 0);
  finder(0, size - 7);
  for (let index = 8; index < size - 8; index += 1) {
    matrix[6][index] = index % 2 ? 0 : 1;
    matrix[index][6] = index % 2 ? 0 : 1;
  }
  return matrix;
}

export function QrCode({ seed }: { seed: string }) {
  const size = 25;
  const matrix = qrMatrix(seed, size);
  const cells: { x: number; y: number }[] = [];
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      if (matrix[y][x]) cells.push({ x, y });
    }
  }
  return (
    <svg
      aria-hidden="true"
      className="vqr"
      shapeRendering="crispEdges"
      viewBox={`0 0 ${size} ${size}`}
    >
      <title>QR</title>
      {cells.map((cell) => (
        <rect height={1} key={`${cell.x}-${cell.y}`} width={1} x={cell.x} y={cell.y} />
      ))}
    </svg>
  );
}
